import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { 
  getAudiosByCategory,
  getRecentlyAddedContent,
  getShortAudiosByCategory,
  getTrendingAudios, 
  getTrendingSeries, 
  getTrendingSeriesByCategory 
} from '../api/homepageApi';
import React from 'react';

// Hook for fetching trending audios
export const useTrendingAudios = () => {
  const { setError } = useAuthStore();

  const {
    data: trendingAudios,
    isLoading,
    error,
    refetch
  } = useQuery('trendingAudios', getTrendingAudios, {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch trending audios';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    trendingAudios,
    isLoading,
    error,
    refetchTrendingAudios: refetch
  };
};

// Hook for fetching all trending series
export const useHomeTrendingSeries = () => {
  const { setError } = useAuthStore();

  const {
    data: trendingSeries,
    isLoading,
    error,
    refetch
  } = useQuery('trendingSeries', getTrendingSeries, {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch trending series';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000,
    cacheTime: 3600000,
  });

  return {
    trendingSeries,
    isLoading,
    error,
    refetchTrendingSeries: refetch
  };
};

// Hook for fetching trending series by category
export const useHomeTrendingSeriesByCategory = (categoryId) => {
  const { setError } = useAuthStore();

  const {
    data: trendingSeriesByCategory,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['trendingSeries', categoryId],
    () => getTrendingSeriesByCategory(categoryId),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch trending series';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: categoryId !== undefined && categoryId !== null,
    }
  );

  // Add some debugging logs
  React.useEffect(() => {
    console.log('Category ID:', categoryId);
    console.log('Is query enabled:', categoryId !== undefined && categoryId !== null);
  }, [categoryId]);

  return {
    trendingSeriesByCategory,
    isLoading,
    error,
    refetchTrendingSeriesByCategory: refetch
  };
};

// New hook for fetching audios by category and audio type
export const useAudiosByCategory = (categoryId, audioType) => {
  const { setError } = useAuthStore();

  const {
    data: audiosByCategory,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['audiosByCategoryHome', categoryId, audioType],
    () => getAudiosByCategory(categoryId, audioType),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch audios by category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: categoryId !== undefined && categoryId !== null && audioType !== undefined && audioType !== null,
    }
  );

  React.useEffect(() => {
    console.log('Category ID:', categoryId, 'Audio Type:', audioType);
    console.log('Is query enabled:', categoryId !== undefined && categoryId !== null && audioType !== undefined && audioType !== null);
  }, [categoryId, audioType]);

  return {
    audiosByCategory,
    isLoading,
    error,
    refetchAudiosByCategory: refetch
  };
};

// Hook for fetching short audios by category
export const useShortAudiosByCategory = (categoryId, page = 1, limit = 10) => {
  const { setError } = useAuthStore();

  const {
    data: shortAudiosByCategory,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['shortAudiosByCategory', categoryId, page, limit],
    () => getShortAudiosByCategory(categoryId, page, limit),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch short audios by category';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000, // Consider data fresh for 5 minutes
      cacheTime: 3600000, // Keep data in cache for 1 hour
      enabled: categoryId !== undefined && categoryId !== null,
    }
  );

  React.useEffect(() => {
    console.log('Short Audios - Category ID:', categoryId, 'Page:', page, 'Limit:', limit);
    console.log('Is query enabled:', categoryId !== undefined && categoryId !== null);
  }, [categoryId, page, limit]);

  return {
    shortAudiosByCategory,
    isLoading,
    error,
    refetchShortAudiosByCategory: refetch
  };
};

// Hook for fetching recently added content
export const useRecentlyAddedContent = () => {
  const { setError } = useAuthStore();

  const {
    data: recentlyAddedContent,
    isLoading,
    error,
    refetch
  } = useQuery('recentlyAddedContent', getRecentlyAddedContent, {
    onError: (error) => {
      console.error('Query error:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to fetch recently added content';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: 1,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  React.useEffect(() => {
    console.log('Recently Added Content loaded:', !!recentlyAddedContent);
  }, [recentlyAddedContent]);

  return {
    recentlyAddedContent,
    isLoading,
    error,
    refetchRecentlyAddedContent: refetch
  };
};